"use client";

import Link from "next/link";
import { useState } from "react";
import { BookIcon, ChevronIcon, FolderIcon } from "@/components/icons";
import type { KnowledgeFolder, KnowledgePageFolder } from "@/lib/kb/folderTypes";
import type { KnowledgeItem, KnowledgeOrganization } from "./useKnowledgeOrganization";

const selectedClassName = "bg-violet-500/10 font-medium text-violet-300";
const actionClassName = "cursor-pointer rounded px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 disabled:cursor-wait disabled:opacity-50";
const dropClassName = "rounded-lg data-[drop-active=true]:bg-violet-500/20 data-[drop-active=true]:ring-1 data-[drop-active=true]:ring-violet-400";

export function KnowledgeFolderTree({ pages, rawSources, folders, pageFolders, organization, isCurrentPage, onCloseDrawer }: {
  pages: string[];
  rawSources: string[];
  folders: KnowledgeFolder[];
  pageFolders: KnowledgePageFolder[];
  organization: KnowledgeOrganization;
  isCurrentPage: (name: string, raw?: boolean) => boolean;
  onCloseDrawer: () => void;
}) {
  const [menu, setMenu] = useState<string | null>(null);
  const folderIds = new Set(folders.map(folder => folder.id));
  const assignments = new Map(pageFolders.map(entry => [JSON.stringify([entry.page, entry.raw]), entry.folderId]));
  const folderFor = (page: string, raw: boolean) => {
    const id = assignments.get(JSON.stringify([page, raw]));
    return id && folderIds.has(id) ? id : null;
  };
  const sorted = [...folders].sort((a, b) => a.name.localeCompare(b.name));
  const choices: { id: string; label: string }[] = [];
  function addChoices(parentId: string | null, path: string, seen: Set<string>) {
    for (const folder of sorted.filter(folder => folder.parentId === parentId)) {
      if (seen.has(folder.id)) continue;
      const label = path ? `${path} / ${folder.name}` : folder.name;
      choices.push({ id: folder.id, label });
      addChoices(folder.id, label, new Set([...seen, folder.id]));
    }
  }
  addChoices(null, "", new Set());

  function movePicker(item: KnowledgeItem, current: string | null, label: string) {
    return (
      <label className="block px-2 py-1 text-xs text-zinc-500">
        Move to
        <select aria-label={`Move ${label} to`} disabled={organization.pending} value={current ?? ""}
          onChange={async event => { if (await organization.move(item, event.target.value || null)) setMenu(null); }}
          className="mt-1 w-full cursor-pointer rounded border border-zinc-700 bg-zinc-900 p-1.5 text-xs text-zinc-200">
          <option value="">Pages (no folder)</option>
          {choices.filter(folder => item.kind !== "folder" || !organization.containsFolder(item.id, folder.id)).map(folder =>
            <option key={folder.id} value={folder.id}>{folder.label}</option>)}
        </select>
      </label>
    );
  }

  function pageRow(page: string, raw: boolean, depth: number) {
    const name = page.replace(/\.md$/i, "");
    const key = `${raw ? "raw" : "page"}:${page}`;
    const active = isCurrentPage(name, raw);
    const item: KnowledgeItem = { kind: "page", page, raw };
    const log = page === "log.md" && !raw;
    return (
      <div key={key}>
        <div className="group flex min-w-0 items-center">
          <Link href={`/knowledge?page=${encodeURIComponent(name)}${raw ? "&raw=1" : ""}`} onClick={onCloseDrawer}
            {...(!log ? organization.dragProps(item) : {})} aria-current={active ? "page" : undefined}
            title={name} style={{ paddingLeft: 28 + depth * 14 }}
            className={`flex h-9 min-w-0 flex-1 items-center gap-1.5 rounded-lg pr-3 transition ${log || raw ? "text-xs" : "text-sm"} ${active ? selectedClassName : raw || log ? "text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-300" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200"}`}>
            <BookIcon className="size-3.5 shrink-0 text-zinc-500" />
            <span className="min-w-0 flex-1 truncate">
              {log ? "log (timeline)" : name}
              {name === "index" && !raw && <span className="ml-1.5 text-[10px] text-zinc-600">entry point</span>}
              {raw && depth > 0 && <span className="ml-1.5 text-[10px] text-zinc-600">source</span>}
            </span>
          </Link>
          {!log && <button type="button" aria-label={`Organize ${raw ? "source " : ""}${name}`} aria-expanded={menu === key}
            onClick={() => setMenu(menu === key ? null : key)}
            className={`${actionClassName} opacity-60 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100`}>⋯</button>}
        </div>
        {menu === key && <div className="mb-1 ml-3 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
          {movePicker(item, folderFor(page, raw), `${raw ? "source " : ""}${name}`)}
        </div>}
      </div>
    );
  }

  function folderRow(folder: KnowledgeFolder, depth: number, ancestors: Set<string>) {
    if (ancestors.has(folder.id)) return null;
    const open = !organization.collapsed.has(folder.id);
    const key = `folder:${folder.id}`;
    return (
      <div key={folder.id} data-knowledge-folder={folder.id}>
        <div {...organization.dropProps(folder.id)} className={`group flex items-center pr-1 text-sm text-zinc-300 hover:bg-zinc-800/60 ${dropClassName}`}
          style={{ paddingLeft: `${4 + depth * 14}px` }}>
          <button type="button" aria-label={`${open ? "Collapse" : "Expand"} ${folder.name}`} aria-expanded={open}
            onClick={() => organization.toggle(folder.id)} {...organization.dragProps({ kind: "folder", id: folder.id })}
            className="flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-1 text-left">
            <span className="flex size-5 shrink-0 items-center justify-center"><ChevronIcon open={open} /></span>
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <FolderIcon className="size-3.5 shrink-0 text-zinc-500" />
              <span className="truncate">{folder.name}</span>
            </span>
          </button>
          <button type="button" aria-label={`Organize folder ${folder.name}`} aria-expanded={menu === key}
            onClick={() => setMenu(menu === key ? null : key)}
            className={`${actionClassName} opacity-60 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100`}>⋯</button>
        </div>
        {menu === key && <div className="mb-1 ml-3 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
          <button type="button" className={actionClassName} disabled={organization.pending}
            onClick={() => { organization.startCreate(folder.id); setMenu(null); }}>New subfolder</button>
          <button type="button" className={actionClassName} disabled={organization.pending}
            onClick={() => { organization.rename(folder); setMenu(null); }}>Rename folder</button>
          {movePicker({ kind: "folder", id: folder.id }, folder.parentId, `folder ${folder.name}`)}
          <button type="button" className={`${actionClassName} hover:text-red-300`} disabled={organization.pending}
            onClick={async () => { if (await organization.remove(folder.id)) setMenu(null); }}>Delete folder</button>
          <p className="px-2 pb-1 text-[11px] text-zinc-600">Deleting a folder keeps its pages.</p>
        </div>}
        {open && <div>
          {sorted.filter(child => child.parentId === folder.id).map(child => folderRow(child, depth + 1, new Set([...ancestors, folder.id])))}
          {pages.filter(page => page !== "log.md" && folderFor(page, false) === folder.id).map(page => pageRow(page, false, depth + 1))}
          {rawSources.filter(page => folderFor(page, true) === folder.id).map(page => pageRow(page, true, depth + 1))}
          {!folders.some(child => child.parentId === folder.id) && !pageFolders.some(entry => entry.folderId === folder.id) &&
            <p className="px-3 py-1 text-xs text-zinc-600" style={{ paddingLeft: `${48 + (depth + 1) * 14}px` }}>Empty folder</p>}
        </div>}
      </div>
    );
  }

  return <nav aria-label="Knowledge pages" className="space-y-0.5">
    {sorted.filter(folder => !folder.parentId || !folderIds.has(folder.parentId)).map(folder => folderRow(folder, 0, new Set()))}
    <div {...organization.dropProps(null)} className={dropClassName} data-knowledge-unfiled="true">
      {folders.length > 0 && <p className="px-2 pb-1 pt-3 text-xs text-zinc-500">Unfiled pages</p>}
      {pages.filter(page => page !== "log.md" && !folderFor(page, false)).map(page => pageRow(page, false, 0))}
      {pageRow("log.md", false, 0)}
      {rawSources.some(page => !folderFor(page, true)) && <>
        <span className="block px-2 pb-1 pt-3 text-sm font-medium text-zinc-200">Raw sources</span>
        {rawSources.filter(page => !folderFor(page, true)).map(page => pageRow(page, true, 0))}
      </>}
    </div>
  </nav>;
}
